"use client"

import { useNavigate } from 'react-router-dom'
import type { Task } from "@/lib/types"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Calendar, Users, Plus, Clock } from 'lucide-react';
import { getStatusColors } from '@/lib/status-colors';
import { formatDate } from "@/lib/time-utils"
import { useOptionalProductionManagement } from "@/lib/production-management-context"

interface KanbanBoardProps {
  tasks: Task[]
  onSelectTask?: (task: Task) => void
  selectedTask?: Task | null
  enableNavigation?: boolean // New prop to enable navigation to TaskPage
}

type KanbanColumn = {
  id: string
  title: string
  status: string // Now represents group identifier
  color: string
  count: number
}

export function KanbanBoard({ tasks, onSelectTask, selectedTask, enableNavigation = false }: KanbanBoardProps) {
  const navigate = useNavigate();
  const productionContext = useOptionalProductionManagement();
  const getUsersByEmails = productionContext?.getUsersByEmails;
  
  // Helper function to get assignee names from emails
  const getAssigneeNames = (assigneeEmails?: string[]): string => {
    if (!assigneeEmails || assigneeEmails.length === 0) return ""
    if (getUsersByEmails) {
      const users = getUsersByEmails(assigneeEmails)
      if (users.length > 0) return users.map(u => u.name).join(", ")
    }
    return `${assigneeEmails.length} assignee${assigneeEmails.length !== 1 ? "s" : ""}`
  }
  
  // Define task status groups - each group contains multiple statuses in specific order
  const statusGroups = [
    {
      id: "to-do",
      title: "To-do",
      color: "border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/50",
      statuses: ["Planned", "Backlog"]
    },
    {
      id: "in-progress", 
      title: "In Progress",
      color: "border-blue-200 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/50",
      statuses: ["Quality Check", "In Progress", "Maintenance"]
    },
    {
      id: "completed",
      title: "Completed", 
      color: "border-green-200 bg-green-50 dark:border-green-700 dark:bg-green-900/50",
      statuses: ["Done", "Archived"]
    }
  ]

  // Create columns for the 3 main groups
  const columns: KanbanColumn[] = statusGroups.map((group) => ({
    id: group.id,
    title: group.title,
    status: group.id, // Use group id as status identifier
    color: group.color,
    count: tasks.filter(t => t.status && group.statuses.includes(t.status)).length
  }))

  const handleTaskClick = (task: Task) => {
    if (enableNavigation) {
      // Navigate to TaskPage with task data in state
      navigate(`/task/${task.id}`, { state: { task } })
    } else {
      // Use the original callback
      onSelectTask?.(task)
    }
  }

  const getTasksForColumn = (groupId: string) => {
    const group = statusGroups.find(g => g.id === groupId)
    if (!group) return []
    
    // Get tasks for this group and sort them by the status order defined in the group
    const groupTasks = tasks.filter(task => task.status && group.statuses.includes(task.status))
    
    // Sort tasks by status order within the group
    return groupTasks.sort((a, b) => {
      const aIndex = group.statuses.indexOf(a.status || '')
      const bIndex = group.statuses.indexOf(b.status || '')
      return aIndex - bIndex
    })
  }

  return (
    <div className="space-y-6">
      {/* Kanban Board Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground dark:text-gray-100">Task Board</h2>
          <p className="text-muted-foreground dark:text-gray-400">
            Organize and track your tasks across different stages
          </p>
        </div>
        <div className="text-sm text-muted-foreground">
          {tasks.length} total tasks
        </div>
      </div>

      {/* Kanban Columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 min-h-[600px]">
        {columns.map((column) => (
          <div key={column.id} className={`rounded-lg border-2 ${column.color} p-4`}>
            {/* Column Header */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-lg">{column.title}</h3>
                <Badge variant="secondary" className="text-xs">
                  {column.count}
                </Badge>
              </div>
              <div className="w-full h-1 bg-current opacity-20 rounded-full"></div>
            </div>

            {/* Column Content */}
            <ScrollArea className="h-[500px] pr-2">
              <div className="space-y-3">{getTasksForColumn(column.id).map((task) => (
                  <Card 
                    key={task.id}
                    className={`border-l-4 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${
                      selectedTask?.id === task.id 
                        ? 'ring-2 ring-blue-500 shadow-md' 
                        : ''
                    }`}
                    style={{ borderLeftColor: getStatusColors(task.status).borderColorValue }}
                    onClick={() => handleTaskClick(task)}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-sm font-medium leading-tight line-clamp-2">
                          {task.title}
                        </CardTitle>
                        <Badge 
                          className={`${getStatusColors(task.status).bg} ${getStatusColors(task.status).text} ${getStatusColors(task.status).border} border shrink-0 text-xs`}
                        >
                          {task.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="pt-0">
                      <div className="space-y-2">
                        {/* Task Description */}
                        {task.description && (
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {task.description}
                          </p>
                        )}
                        
                        {/* Task Meta Information */}
                        <div className="space-y-1">
                          {task.due && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Calendar className="h-3 w-3" />
                              <span>{formatDate(task.due)}</span>
                            </div>
                          )}
                          
                          {(task.estimatedHours !== undefined || task.realisedHours !== undefined) && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              {task.realisedHours !== undefined && task.estimatedHours !== undefined ? (
                                <span>{task.realisedHours.toFixed(1)}h / {task.estimatedHours}h</span>
                              ) : task.realisedHours !== undefined ? (
                                <span>{task.realisedHours.toFixed(1)}h tracked</span>
                              ) : (
                                <span>{task.estimatedHours}h est.</span>
                              )}
                            </div>
                          )}
                          
                          {task.assignee && task.assignee.length > 0 && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Users className="h-3 w-3" />
                              <span className="font-medium">{getAssigneeNames(task.assignee)}</span>
                            </div>
                          )}
                          
                          {task.contributors && task.contributors.length > 0 && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Users className="h-3 w-3" />
                              <span>
                                {task.contributors.length} contributor{task.contributors.length > 1 ? 's' : ''}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Task Tags */}
                        {task.tags && task.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {task.tags.slice(0, 2).map((tag, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                            {task.tags.length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{task.tags.length - 2}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}                {/* Empty State for Column */}
                {getTasksForColumn(column.id).length === 0 && (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                      <Plus className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <p className="text-sm text-muted-foreground">No tasks in {column.title.toLowerCase()}</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        ))}
      </div>
    </div>
  )
}